import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, Form, Input, Button, Typography, Row, Col, message, Space } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import loginImage from "../Assets/login.jpg";

const { Title, Text } = Typography;

const Login = () => {
  const [stage, setStage] = useState("login"); // 'login', 'requestOtp', 'resetPassword'
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoggedIn) {
      navigate("/HomePage");
    }
  }, [isLoggedIn, navigate]);

  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      const response = await fetch("https://api.21stcenturyav.info/login/user", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          username: values.email,
          password: values.password,
        }),
      });

      if (!response.ok) {
        throw new Error("Incorrect username or password");
      }

      const data = await response.json();
      localStorage.setItem("accessToken", data.access_token);
      message.success("Login Successful!");
      setIsLoggedIn(true);
    } catch (error) {
      message.error("Login Failed: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestOtp = async (values) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `https://api.21stcenturyav.info/password-reset/request?email=${values.email}`,
        { method: "POST" }
      );

      if (!response.ok) {
        throw new Error("Failed to send OTP");
      }

      message.success("OTP sent to your email. Please verify to continue.");
      setStage("resetPassword");
    } catch (error) {
      message.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (values) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `https://api.21stcenturyav.info/password-reset/verify?email=${values.email}&otp=${otp.join(
          ""
        )}&new_password=${values.password}`,
        { method: "POST" }
      );

      if (!response.ok) {
        throw new Error("Failed to reset password");
      }

      message.success("Password reset successfully!");
      setStage("login");
    } catch (error) {
      message.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const renderForm = () => {
    switch (stage) {
      case "requestOtp":
        return (
          <Form layout="vertical" onFinish={handleRequestOtp}>
            <Title level={3}>Request OTP</Title>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input placeholder="Enter your email" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={isLoading} block>
                Send OTP
              </Button>
            </Form.Item>
          </Form>
        );
      case "resetPassword":
        return (
          <Form layout="vertical" onFinish={handlePasswordReset}>
            <Title level={3}>Reset Password</Title>
            <Text>Enter the 6-digit OTP sent to your email</Text>
            <Space>
              {otp.map((_, index) => (
                <Input
                  key={index}
                  maxLength={1}
                  value={otp[index] || ""}
                  onChange={(e) => {
                    const newOtp = [...otp];
                    newOtp[index] = e.target.value;
                    setOtp(newOtp);
                  }}
                  style={{
                    width: "40px",
                    textAlign: "center",
                    marginTop: "10px",
                  }}
                />
              ))}
            </Space>
            <Form.Item
              name="password"
              label="New Password"
              rules={[{ required: true, message: "Please enter a new password" }]}
            >
              <Input.Password
                placeholder="Enter new password"
                iconRender={(visible) =>
                  visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                }
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={isLoading} block>
                Verify and Update
              </Button>
            </Form.Item>
          </Form>
        );
      default:
        return (
          <Form layout="vertical" onFinish={handleSubmit}>
            <Title level={3}>Welcome Back!</Title>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input placeholder="Enter your email" />
            </Form.Item>
            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: "Please enter your password" }]}
            >
              <Input.Password
                placeholder="Enter your password"
                iconRender={(visible) =>
                  visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                }
              />
            </Form.Item>
            <Form.Item>
              <Link onClick={() => setStage("requestOtp")}>Forgot password?</Link>
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={isLoading} block>
                Login
              </Button>
            </Form.Item>
            <Form.Item>
              <Text type="secondary">
                Don't have an account? <Link to="/signup">Sign up</Link>
              </Text>
            </Form.Item>
          </Form>
        );
    }
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        backgroundColor: "#f0f2f5",
        padding: "20px",
      }}
    >
      <Card
        style={{
          maxWidth: 600,
          width: "100%",
          borderRadius: "10px",
          boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
        }}
        bodyStyle={{ padding: "40px" }}
      >
        {renderForm()}
      </Card>
    </div>
  );
};

export default Login;
